import { FaArrowCircleLeft } from "react-icons/fa";
import * as url from '@api/requests';
import './ISCODetails.scss';
import { useContext, useEffect, useMemo, useState } from "react";
import { Timelines } from "@config/timelines";
import { CountriesContext } from "@providers/Countries";
import { ISCOArchetypeContext } from "@providers/ISCOArchetype";
import OccupationDetailsCard from "./OccupationDetailsCard";
import { DefaultAvatarsClassification } from '@config/avatars-classification';

type ISCODetailsProps = {
    handleGoBackToChildISCOChart: () => void;
    countryCode: string;
};

const ISCODetails = ({ handleGoBackToChildISCOChart, countryCode }: ISCODetailsProps) => {
    const { countryDetails } = useContext(CountriesContext);
    const { selectedChildISCO } = useContext(ISCOArchetypeContext);

    const [iscoData, setISCOData] = useState<any>(null);
    const [isLoadingOccupationCard, setIsLoadingOccupationCard] = useState<boolean>(false);
    const [isLoadingExportISCO4, setIsLoadingExportISCO4] = useState<boolean>(false);
    const [fromTimeline, setFromTimeline] = useState<string>("");
    const [toTimeline, setToTimeline] = useState<string>("");

    const countryData = useMemo(() => {
        return (
            countryDetails?.find(({ code }: any) => code.toLowerCase() === countryCode) || null
        );
    }, [countryCode, countryDetails]);

    useEffect(() => {
        const fetchData = async () => {
            const currentTimeline = Timelines.find(t => t.hubId === countryData.hubId);
            if (currentTimeline) {
                // Get last year data
                const tempFromTimeline = currentTimeline.timelines[currentTimeline.timelines.length - 4];
                setFromTimeline(tempFromTimeline);
                const tempToTimeline = currentTimeline.timelines[currentTimeline.timelines.length - 1];
                setToTimeline(tempToTimeline);

                setIsLoadingOccupationCard(true);

                const [errMostRequiredISCO4JobInfo, incomingMostRequiredISCO4JobInfo] = await url.getMostRequiredISCO4JobInfo([countryData.hubId], [], [], [selectedChildISCO.isco4_code], [], tempFromTimeline, tempToTimeline, false);
                if (errMostRequiredISCO4JobInfo) {
                    console.warn(errMostRequiredISCO4JobInfo);
                } else {
                    setISCOData && setISCOData(incomingMostRequiredISCO4JobInfo);
                }

                setIsLoadingOccupationCard(false);
            }
        };
        if (selectedChildISCO && selectedChildISCO.isco4_code && countryData && countryData.hubId) {
            fetchData();
        }
    }, [selectedChildISCO, countryData, setFromTimeline, setToTimeline]);

    const exportISCO4AsPDF = async () => {
        setIsLoadingExportISCO4(true);
        const [errExportedISCO4, incomingExportedISCO4] = await url.exportISCO4([countryData.hubId], [], [], [selectedChildISCO.isco4_code], [], fromTimeline, toTimeline, undefined, undefined, false);
        if (errExportedISCO4) console.warn(errExportedISCO4);
        if (incomingExportedISCO4) {
            const url = window.URL.createObjectURL(new Blob([incomingExportedISCO4]));
            const link = document.createElement("a");
            link.href = url;
            link.setAttribute("download", "isco4-" + selectedChildISCO?.isco4_label?.replace(/(^\w|\s\w)/g, (m: any) => m.toUpperCase()).replaceAll(" ", "") + "-" + countryData.country.replaceAll(" ", "") + ".pdf");
            document.body.appendChild(link);
            link.click();
        }
        setIsLoadingExportISCO4(false);
    }

    return <>
        {selectedChildISCO &&
            <>
                <div className="isco-details-container">
                    <div className="isco-details-basic-info">
                        <div onClick={handleGoBackToChildISCOChart} className="isco-details-back-button-container">
                            <FaArrowCircleLeft className="isco-details-back-button-arrow" />
                        </div>
                        {selectedChildISCO &&
                            <>
                                <div className="isco-details-basic-info-image-container">
                                    <img src={process.env.REACT_APP_CHAT_ISCO_ARCHETYPE_URL + DefaultAvatarsClassification[countryCode].theme + "/" + DefaultAvatarsClassification[countryCode].region + "/" + DefaultAvatarsClassification[countryCode].ethnicity + "/" + selectedChildISCO.isco4_code + ".png"} width="200px" height="200px" alt="" />
                                </div>
                                {selectedChildISCO.isco4_tasks && <div className="isco-details-basic-info-description">{selectedChildISCO.isco4_tasks}</div>}
                            </>
                        }
                    </div>
                    <div className="isco-details-info">
                        <OccupationDetailsCard isLoadingOccupationCard={isLoadingOccupationCard} isLoadingDownloadISCOCard={isLoadingExportISCO4} data={iscoData} handleDownloadISCOPDF={exportISCO4AsPDF} />
                    </div>
                </div>
            </>
        }
    </>
};

export default ISCODetails;
